import { isCoarsePointer, shouldSendOnEnter } from "@recruiter/shared";
import { useRef, useState } from "react";

import { useSendSellerMessage } from "../../lib/mutations/seller";
import { SellerQueryError } from "../../lib/queries/seller";
import { postSellerTyping, uploadSellerFile } from "../../lib/seller-client";

// One file the talent has uploaded and is about to attach to their next message.
// `id` is the server-stored seller_files id (linked to the message on send);
// `name` is the picked filename, for the tray chip.
type Pending = { id: string; name: string };

// The talent's reply box. Sends the message (202 → enqueues STERLING's turn) and,
// optionally, files the talent attached: those are uploaded up-front (POST
// .../files → stored + scanned + extracted) and their ids ride the send so the
// backend links them to the message and STERLING reads them (load_seller_files).
// Errors surface inline, never swallowed (doctrine 1.5).
export function ReplyInput({ threadId, sellerId }: { threadId: string; sellerId: string }) {
  const [text, setText] = useState("");
  const [pending, setPending] = useState<Pending[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mutation = useSendSellerMessage();
  // Throttle the "I'm composing" ping to at most once per 3s while typing; the
  // server's 8s freshness window + the client-side decay clear it when we stop.
  const lastTypingPingRef = useRef(0);

  function onChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
    setText(e.target.value);
    const now = Date.now();
    if (now - lastTypingPingRef.current > 3000) {
      lastTypingPingRef.current = now;
      void postSellerTyping(threadId, sellerId);
    }
  }

  async function onFilesPicked(list: FileList | null): Promise<void> {
    const files = list ? Array.from(list) : [];
    // Reset the input so picking the same file again re-fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (files.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      const outcome = await uploadSellerFile(threadId, files, sellerId);
      if (!outcome.ok) {
        setUploadError(outcome.message);
        return;
      }
      const rejected = outcome.data.rejected ?? [];
      // The backend stores files in order, skipping rejected ones — so the
      // returned ids line up with the accepted files in the same order.
      const rejectedNames = new Set(rejected.map((r) => r.file_name));
      const accepted = files.filter((f) => !rejectedNames.has(f.name));
      const ids = outcome.data.uploaded_file_ids ?? [];
      setPending((prev) => [
        ...prev,
        ...ids.map((id, i) => ({ id, name: accepted[i]?.name ?? "file" })),
      ]);
      if (rejected.length > 0) {
        setUploadError(rejected.map((r) => `${r.file_name}: ${r.reason}`).join("; "));
      }
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : "Couldn't upload that file.");
    } finally {
      setUploading(false);
    }
  }

  function removePending(id: string): void {
    setPending((prev) => prev.filter((p) => p.id !== id));
  }

  async function send(): Promise<void> {
    const body = text.trim();
    if ((!body && pending.length === 0) || mutation.isPending || uploading) return;
    try {
      await mutation.mutateAsync({
        threadId,
        body,
        sellerId,
        attachmentFileIds: pending.map((p) => p.id),
      });
      setText("");
      setPending([]);
      setUploadError(null);
    } catch {
      // Surfaced via mutation.error below — never swallowed.
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    // Desktop: Enter sends; Shift+Enter newlines. Touch: no Shift key exists,
    // so Enter always newlines and only the send button sends.
    if (shouldSendOnEnter(e, isCoarsePointer())) {
      e.preventDefault();
      void send();
    }
  }

  const sendDisabled = mutation.isPending || uploading || (!text.trim() && pending.length === 0);
  const errorMessage =
    uploadError ??
    (mutation.error instanceof SellerQueryError
      ? mutation.error.message
      : mutation.error
        ? "Couldn't send your reply. Try again."
        : null);

  return (
    <div>
      {pending.length > 0 || uploading ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--rc-space-2)",
            alignItems: "center",
            padding: "var(--rc-space-2) var(--rc-space-6) 0",
          }}
        >
          {pending.map((p) => (
            <span
              key={p.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "var(--rc-space-2)",
                padding: "2px 8px",
                borderRadius: "6px",
                border: "1px solid var(--rc-color-border, rgba(128,128,128,0.4))",
                fontSize: "0.85em",
                maxWidth: "16rem",
              }}
            >
              <span
                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                📎 {p.name}
              </span>
              <button
                type="button"
                aria-label={`Remove ${p.name}`}
                onClick={() => removePending(p.id)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  lineHeight: 1,
                  padding: 0,
                  color: "inherit",
                }}
              >
                ×
              </button>
            </span>
          ))}
          {uploading ? (
            <span style={{ fontSize: "0.85em", opacity: 0.7 }}>uploading…</span>
          ) : null}
        </div>
      ) : null}
      <div className="sl-composer">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => void onFilesPicked(e.target.files)}
          aria-hidden="true"
        />
        <button
          className="sl-btn"
          type="button"
          disabled={mutation.isPending || uploading}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach a file"
          title="Attach a file"
        >
          📎
        </button>
        <textarea
          value={text}
          placeholder="Reply to the concierge… (price, timeline, scope)"
          disabled={mutation.isPending}
          onChange={onChange}
          onKeyDown={onKeyDown}
          aria-label="Your reply"
        />
        <button className="sl-btn" disabled={sendDisabled} onClick={() => void send()}>
          {mutation.isPending ? "Sending…" : "Send"}
        </button>
      </div>
      {errorMessage ? (
        <div
          className="sl-inline-error"
          style={{ padding: "0 var(--rc-space-6) var(--rc-space-2)" }}
        >
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
}
